import './Modals.scss';
import { useEffect, useState } from 'react';
import { Button, DatePicker, Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { ModalProps } from '../../utils/types';
import { Notify } from '../../utils/constants';
import { apiClient } from '../../utils/httpClient';
import {
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  employeeStatusParam,
} from '../Filters/EmployeeStatusFilter';

type MonthlyExportModalProps = ModalProps & {
  // Pre-selects the Active filter of the list the modal was opened from.
  employeeStatusFilter?: EmployeeStatusFilterValue;
};

const MonthlyExportModal = ({
  isOpen,
  setIsOpen,
  employeeStatusFilter = DEFAULT_EMPLOYEE_STATUS_FILTER,
}: MonthlyExportModalProps) => {
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [employeeStatus, setEmployeeStatus] =
    useState<EmployeeStatusFilterValue>(employeeStatusFilter);

  useEffect(() => {
    if (isOpen) setEmployeeStatus(employeeStatusFilter);
  }, [isOpen, employeeStatusFilter]);

  const handleDownload = async () => {
    if (!selectedMonth) {
      Notify('info', 'Please select a month.');
      return;
    }

    const month = selectedMonth.month() + 1;
    const year = selectedMonth.year();

    try {
      setIsExporting(true);
      const response = await apiClient.get(
        `${process.env.REACT_APP_API_URL}/exportLeaveRequests/monthly`,
        {
          params: {
            month,
            year,
            employeeStatus: employeeStatusParam(employeeStatus),
          },
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.ms-excel',
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(
        /filename="?([^";\n]+)"?/,
      );
      const filename = filenameMatch
        ? filenameMatch[1]
        : `leave_monthly_${String(month).padStart(2, '0')}.${year}.xls`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to export monthly leave requests:', error);
      Notify('error', 'Could not export monthly leave requests.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      title="Export monthly leave requests"
      width={360}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={isExporting}
        >
          Download
        </Button>,
      ]}
    >
      <DatePicker
        picker="month"
        format="MM/YYYY"
        value={selectedMonth}
        onChange={(date) => setSelectedMonth(date)}
        placeholder="Select month"
        style={{ width: '100%' }}
      />
      <EmployeeStatusFilter
        className="mt-3"
        value={employeeStatus}
        onChange={setEmployeeStatus}
      />
    </Modal>
  );
};

export default MonthlyExportModal;
